import {
    AppleIcon,
    ArrowRightLeftIcon,
    CalculatorIcon,
    CalendarCheckIcon,
    CalendarDaysIcon,
    ChessKnightIcon,
    CircleIcon,
    Columns2Icon,
    CopyIcon,
    DicesIcon,
    DropletIcon,
    FileTextIcon,
    FishIcon,
    FlameIcon,
    Gamepad2Icon,
    GemIcon,
    GraduationCapIcon,
    HeartIcon,
    LeafIcon,
    LightbulbIcon,
    ListChecksIcon,
    LoaderCircleIcon,
    MapIcon,
    PiggyBankIcon,
    PuzzleIcon,
    RadicalIcon,
    ReceiptIcon,
    RocketIcon,
    RouteIcon,
    ShoppingCartIcon,
    ShuffleIcon,
    SmileIcon,
    SquareIcon,
    StarIcon,
    StickyNoteIcon,
    TargetIcon,
    TimerIcon,
    TreePineIcon,
    WalletIcon,
    WavesIcon,
    type LucideIcon
} from 'lucide-react';
import {
    ChessBoardIcon,
    HourglassHalfIcon,
    RunningIcon,
    WaveSquareIcon
} from '../shared/customIcons.ts';
import {TemplateKind, WizardDataState} from './vibe.ts';

export type GameCategory =
    | 'Arcade & Action'
    | 'Puzzle & Memory'
    | 'Creative & Ambient';

export type AppCategory =
    | 'Tasks & Lists'
    | 'Focus & Learning'
    | 'Calculators & Tools'
    | 'Trackers & Journals';

export type TemplateCategory = GameCategory | AppCategory;

export const gameCategories: GameCategory[] = [
    'Arcade & Action',
    'Puzzle & Memory',
    'Creative & Ambient'
];

export const appCategories: AppCategory[] = [
    'Tasks & Lists',
    'Focus & Learning',
    'Calculators & Tools',
    'Trackers & Journals'
];

export interface BuilderTemplate {
    icon: LucideIcon;
    title: string;
    description: string;
    kind: TemplateKind;
    category: TemplateCategory;
    details: WizardDataState;
}

// A seed is a template without its `kind` — kind is injected per group below.
type TemplateSeed = Omit<BuilderTemplate, 'kind'>;

const gameTemplates: TemplateSeed[] = [
    {
        icon: Gamepad2Icon,
        title: 'Breakout Game',
        description: 'Classic brick-breaking fun with a paddle and ball.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Brick Breaker',
            objective:
                'Player controls a paddle to bounce a ball and break all the bricks.',
            coreMechanics:
                'Paddle movement via mouse/arrow keys, ball physics with realistic bouncing, brick collision, scoring system, three lives.',
            visualStyle: 'Retro arcade with bright colors.',
            keyElements:
                'Score display, lives display, start button, game over message, win condition.'
        }
    },
    {
        icon: RocketIcon,
        title: 'Space Shooter',
        description: 'Defend the cosmos by shooting asteroids and enemy ships.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Cosmic Defender',
            objective:
                'Player controls a spaceship to shoot incoming asteroids and enemy ships while avoiding collisions.',
            coreMechanics:
                'Ship movement via arrow keys, shooting with spacebar, enemy spawning system, collision detection, scoring.',
            visualStyle: 'Modern space theme with particle effects.',
            keyElements:
                'Health bar, score counter, power-ups, increasing difficulty, game over screen.'
        }
    },
    {
        icon: LightbulbIcon,
        title: 'Quiz Game',
        description:
            'Test your knowledge with multiple-choice questions against the clock.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Brain Challenge',
            objective:
                'Player answers multiple-choice questions to earn points before time runs out.',
            coreMechanics:
                'Multiple-choice selection, timer, scoring system, category selection.',
            visualStyle:
                'Clean, educational interface with color-coded feedback.',
            keyElements:
                'Question display, answer options, timer, score tracking, final results screen, 10 sample general knowledge questions.'
        }
    },
    {
        icon: AppleIcon,
        title: 'Snake Game',
        description:
            'Guide your snake to eat food and grow without hitting walls or yourself.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Snake Classic',
            objective:
                'Control a snake to eat food items and grow longer while avoiding collisions with walls and your own body.',
            coreMechanics:
                'Arrow key movement, food spawning, collision detection, length growth, increasing speed.',
            visualStyle:
                'Retro green-on-black aesthetic with pixel-style graphics.',
            keyElements:
                'Grid-based movement, score display, high score tracking, game over screen, restart button.'
        }
    },
    {
        icon: PuzzleIcon,
        title: 'Memory Match',
        description:
            'Flip cards to find matching pairs in this classic memory game.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Card Memory',
            objective:
                'Find all matching pairs of cards by flipping them two at a time within the time limit.',
            coreMechanics:
                'Card flipping, pair matching logic, timer countdown, move counter, shuffle algorithm.',
            visualStyle: 'Colorful card designs with smooth flip animations.',
            keyElements:
                '16 cards (8 pairs), flip animation, match celebration, timer display, moves counter, completion screen.'
        }
    },
    {
        icon: CircleIcon,
        title: 'Pong Game',
        description: 'Classic two-paddle tennis game with AI opponent.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Digital Pong',
            objective:
                "Score points by hitting the ball past your opponent's paddle using your paddle.",
            coreMechanics:
                'Paddle movement, ball physics, AI opponent, collision detection, scoring system.',
            visualStyle: 'Minimalist black and white retro design.',
            keyElements:
                'Two paddles, bouncing ball, score display, AI difficulty, first-to-10 wins, serve system.'
        }
    },
    {
        icon: SquareIcon,
        title: 'Tetris Clone',
        description:
            'Stack falling blocks to clear lines in this puzzle classic.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Block Drop',
            objective:
                'Arrange falling tetromino pieces to form complete horizontal lines that disappear.',
            coreMechanics:
                'Piece rotation, line clearing, increasing speed, piece preview, hold function.',
            visualStyle:
                'Colorful blocks with clean grid layout and smooth animations.',
            keyElements:
                'Game grid, next piece preview, score system, level progression, line clear effects.'
        }
    },
    {
        icon: TargetIcon,
        title: 'Whack-a-Mole',
        description: 'Hit the moles as they pop up from their holes!',
        category: 'Arcade & Action',
        details: {
            gameName: 'Mole Whacker',
            objective:
                'Click on moles as they randomly appear from holes to score points before time runs out.',
            coreMechanics:
                'Random mole spawning, click detection, timer system, scoring, increasing difficulty.',
            visualStyle: 'Cartoon-style with bright colors and fun animations.',
            keyElements:
                '9 holes in 3x3 grid, mole pop-up animations, hammer cursor, score display, 60-second timer.'
        }
    },
    {
        icon: ShuffleIcon,
        title: 'Simon Says',
        description:
            'Remember and repeat the color sequence in this memory challenge.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Color Memory',
            objective:
                'Watch the sequence of colored buttons light up, then repeat the pattern by clicking them in order.',
            coreMechanics:
                'Sequence generation, pattern playback, input validation, progressive difficulty, sound effects.',
            visualStyle:
                'Four large colored buttons with glowing effects and audio feedback.',
            keyElements:
                '4 colored buttons, sequence display, sound cues, round counter, game over on wrong input.'
        }
    },
    {
        icon: ChessKnightIcon,
        title: 'Tic Tac Toe',
        description: 'Get three in a row in this classic strategy game.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Noughts & Crosses',
            objective:
                'Place your X or O to get three in a row horizontally, vertically, or diagonally before your opponent.',
            coreMechanics:
                'Turn-based gameplay, win condition checking, AI opponent, grid interaction.',
            visualStyle: 'Clean minimal design with smooth X and O animations.',
            keyElements:
                '3x3 grid, player vs AI, win detection, draw detection, score tracking, reset button.'
        }
    },
    {
        icon: DicesIcon,
        title: 'Number Puzzle',
        description: 'Slide numbered tiles to arrange them in order.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Sliding Puzzle',
            objective:
                'Slide numbered tiles in a 4x4 grid to arrange them in numerical order from 1-15.',
            coreMechanics:
                'Tile sliding logic, valid move detection, win condition, shuffle algorithm, move counter.',
            visualStyle: 'Clean numbered tiles with smooth sliding animations.',
            keyElements:
                '4x4 grid with 15 tiles, empty space, move counter, shuffle button, victory screen.'
        }
    },
    {
        icon: FlameIcon,
        title: 'Reaction Test',
        description:
            'Test your reflexes by clicking when the screen changes color.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Reflex Challenge',
            objective:
                'Click as quickly as possible when the screen changes from red to green to test reaction time.',
            coreMechanics:
                'Random delay timing, click detection, reaction time calculation, false start detection.',
            visualStyle:
                'Full-screen color changes with large, clear instructions.',
            keyElements:
                'Color-changing background, reaction time display, average calculation, best time tracking, retry button.'
        }
    },
    {
        icon: HeartIcon,
        title: 'Matching Hearts',
        description: 'Connect matching colored hearts before time runs out.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Heart Connect',
            objective:
                'Draw lines to connect pairs of matching colored hearts on the grid before the timer expires.',
            coreMechanics:
                'Line drawing, path validation, color matching, timer countdown, level progression.',
            visualStyle:
                'Romantic theme with colorful hearts and smooth line animations.',
            keyElements:
                'Grid of colored hearts, line drawing tool, timer display, level progression, completion effects.'
        }
    },
    {
        icon: GemIcon,
        title: 'Gem Collector',
        description: 'Navigate through a maze to collect all the gems.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Treasure Hunt',
            objective:
                'Move your character through the maze using arrow keys to collect all gems while avoiding obstacles.',
            coreMechanics:
                'Character movement, collision detection, gem collection, maze navigation, obstacle avoidance.',
            visualStyle:
                'Top-down view with colorful gems and simple maze design.',
            keyElements:
                'Maze layout, player character, collectible gems, obstacles, gem counter, completion celebration.'
        }
    },
    {
        icon: LoaderCircleIcon,
        title: 'Word Spinner',
        description: 'Unscramble letters to form words against the clock.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Letter Twist',
            objective:
                'Rearrange scrambled letters to form valid words before time runs out.',
            coreMechanics:
                'Letter scrambling, word validation, drag-and-drop or click reordering, timer system, scoring.',
            visualStyle:
                'Clean typography with letter tiles and smooth reordering animations.',
            keyElements:
                'Scrambled letter tiles, word input area, timer, score system, hint button, 20 common words to unscramble.'
        }
    },
    {
        icon: RouteIcon,
        title: 'Maze Runner',
        description:
            'Navigate through randomly generated mazes to reach the exit.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Labyrinth Escape',
            objective:
                'Guide your character through procedurally generated mazes to find the exit door.',
            coreMechanics:
                'Arrow key movement, maze generation algorithm, collision detection, exit detection, increasing maze complexity.',
            visualStyle:
                'Top-down view with stone walls and torch lighting effects.',
            keyElements:
                'Player dot, maze walls, exit portal, mini-map, level counter, completion time tracking.'
        }
    },
    {
        icon: RunningIcon,
        title: 'Jump & Run',
        description:
            'Classic side-scrolling platformer with jumping and collecting.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Platform Hero',
            objective:
                'Jump between platforms, avoid obstacles, and collect coins to reach the flag at each level.',
            coreMechanics:
                'Gravity physics, jump mechanics, platform collision, coin collection, enemy avoidance.',
            visualStyle:
                'Colorful 2D side-view with retro pixel art aesthetic.',
            keyElements:
                'Jumping character, platforms, collectible coins, moving obstacles, flag goal, lives system.'
        }
    },
    {
        icon: TreePineIcon,
        title: 'Forest Gatherer',
        description: 'Collect resources in a peaceful forest environment.',
        category: 'Creative & Ambient',
        details: {
            gameName: 'Nature Collector',
            objective:
                'Move around a forest area to gather berries, wood, and stones to reach collection goals.',
            coreMechanics:
                'Resource spawning, collection on contact, inventory tracking, goal completion, peaceful exploration.',
            visualStyle:
                'Soft natural colors with trees, bushes, and animated resource items.',
            keyElements:
                'Forest environment, collectible resources, inventory display, collection goals, calm background music.'
        }
    },
    {
        icon: GemIcon,
        title: 'Crystal Miner',
        description: 'Dig through layers to find valuable crystals and gems.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Underground Explorer',
            objective:
                'Click to dig through dirt layers and discover hidden crystals while managing your energy.',
            coreMechanics:
                'Click-to-dig mechanic, resource discovery, energy management, depth progression, crystal values.',
            visualStyle:
                'Cross-section underground view with earthy tones and sparkling gems.',
            keyElements:
                'Diggable terrain, hidden crystals, energy bar, depth meter, crystal collection display.'
        }
    },
    {
        icon: WavesIcon,
        title: 'Flowing Colors',
        description:
            'Watch mesmerizing color patterns flow and blend across the screen.',
        category: 'Creative & Ambient',
        details: {
            gameName: 'Liquid Dreams',
            objective:
                'No objective - simply enjoy watching fluid color simulations respond to mouse movement.',
            coreMechanics:
                'Fluid simulation, color blending, mouse interaction, particle effects, smooth animations.',
            visualStyle:
                'Vibrant flowing colors with smooth gradients and liquid-like movement.',
            keyElements:
                'Mouse-responsive particles, color transitions, full-screen canvas, ambient sound option.'
        }
    },
    {
        icon: CircleIcon,
        title: 'Bouncing Orbs',
        description: 'Peaceful animation of colorful spheres bouncing around.',
        category: 'Creative & Ambient',
        details: {
            gameName: 'Zen Spheres',
            objective:
                'No objective - watch colorful orbs bounce naturally with physics-based movement.',
            coreMechanics:
                'Physics simulation, collision detection, color cycling, size variation, gentle bouncing.',
            visualStyle:
                'Soft pastel colors with glowing orbs and subtle shadow effects.',
            keyElements:
                'Multiple bouncing spheres, color gradients, physics boundaries, size variations, click to add orbs.'
        }
    },
    {
        icon: StarIcon,
        title: 'Kaleidoscope',
        description: 'Interactive geometric patterns that shift and change.',
        category: 'Creative & Ambient',
        details: {
            gameName: 'Pattern Dreams',
            objective:
                'No objective - create and watch evolving symmetrical patterns and mandalas.',
            coreMechanics:
                'Mathematical pattern generation, symmetry algorithms, color rotation, mouse interaction.',
            visualStyle:
                'Bright geometric shapes with perfect symmetry and rainbow color cycling.',
            keyElements:
                'Symmetrical patterns, color cycling, mouse interaction, pattern complexity slider.'
        }
    },
    {
        icon: MapIcon,
        title: 'Maze Builder',
        description: 'Create and solve your own custom mazes.',
        category: 'Puzzle & Memory',
        details: {
            gameName: 'Maze Architect',
            objective:
                'Draw walls to create maze paths, then test your creation by solving it.',
            coreMechanics:
                'Wall drawing/erasing, path validation, solve mode, maze testing, save/load functionality.',
            visualStyle:
                'Clean grid-based design with clear wall and path distinction.',
            keyElements:
                'Grid canvas, drawing tools, play/build mode toggle, start/end points, solution path display.'
        }
    },
    {
        icon: LeafIcon,
        title: 'Garden Grower',
        description: 'Plant seeds and watch your garden grow over time.',
        category: 'Creative & Ambient',
        details: {
            gameName: 'Bloom Paradise',
            objective:
                'Click to plant different seeds and watch them grow into flowers while managing water and sunlight.',
            coreMechanics:
                'Seed planting, growth timers, resource management, garden expansion, flower variety.',
            visualStyle:
                'Bright garden view with colorful flowers and growth animations.',
            keyElements:
                'Plantable garden plots, seed inventory, growth stages, watering can, sunshine meter.'
        }
    },
    {
        icon: WaveSquareIcon,
        title: 'Wave Patterns',
        description: 'Hypnotic wave animations with customizable frequencies.',
        category: 'Creative & Ambient',
        details: {
            gameName: 'Sine Waves',
            objective:
                'No objective - adjust wave parameters to create mesmerizing sine wave patterns.',
            coreMechanics:
                'Mathematical wave generation, frequency controls, amplitude adjustment, color shifting.',
            visualStyle:
                'Smooth flowing waves with gradient colors and rhythmic movement.',
            keyElements:
                'Multiple wave layers, frequency sliders, color controls, full-screen wave display.'
        }
    },
    {
        icon: ChessBoardIcon,
        title: 'Tile Jumper',
        description: 'Hop across disappearing tiles to reach the other side.',
        category: 'Arcade & Action',
        details: {
            gameName: 'Vanishing Path',
            objective:
                'Jump from tile to tile across a grid before they disappear, reaching the safe zone.',
            coreMechanics:
                'Tile timer system, jump validation, path planning, tile regeneration, increasing difficulty.',
            visualStyle:
                'Isometric view with colorful tiles that fade as time passes.',
            keyElements:
                'Grid of timed tiles, player piece, safe zones, tile countdown indicators, level progression.'
        }
    },
    {
        icon: FishIcon,
        title: 'Aquarium Life',
        description:
            'Peaceful fish swimming simulation with feeding interaction.',
        category: 'Creative & Ambient',
        details: {
            gameName: 'Digital Aquarium',
            objective:
                'No objective - watch fish swim naturally and click to drop food for them to eat.',
            coreMechanics:
                'Fish AI movement, feeding interaction, bubble effects, fish variety, peaceful ambiance.',
            visualStyle:
                'Underwater blue tones with colorful fish and gentle bubble animations.',
            keyElements:
                'Swimming fish, click-to-feed, bubble effects, aquarium decorations, ambient water sounds.'
        }
    }
];

const appTemplates: TemplateSeed[] = [
    {
        icon: ListChecksIcon,
        title: 'To-Do List',
        description:
            'Capture tasks, check them off, and pick up where you left off.',
        category: 'Tasks & Lists',
        details: {
            gameName: 'Task Master',
            objective:
                'Let users capture tasks, mark them complete, and keep their list between visits.',
            coreMechanics:
                'Type a task and press Enter to add it, click a checkbox to complete, click text to edit inline, remove with a delete button; filter by All / Active / Completed.',
            visualStyle:
                'Clean minimal interface with a prominent input at the top and a tidy list below.',
            keyElements:
                'Saves all tasks to the browser localStorage so they persist across reloads, active and completed counts, filter buttons, clear-completed button, friendly empty-state message.'
        }
    },
    {
        icon: Columns2Icon,
        title: 'Kanban Board',
        description: 'Drag task cards across To Do, Doing and Done columns.',
        category: 'Tasks & Lists',
        details: {
            gameName: 'Flow Board',
            objective:
                'Organise tasks across To Do, Doing, and Done columns by dragging cards between them.',
            coreMechanics:
                'Add a card to any column, drag-and-drop cards between columns using HTML5 drag events, edit card text inline, delete cards.',
            visualStyle:
                'Three-column card board with subtle shadows and a color accent per column.',
            keyElements:
                'Persists the full board to localStorage so it reloads exactly as left, drag-and-drop between columns, per-column card counts, add/edit/delete cards.'
        }
    },
    {
        icon: ShoppingCartIcon,
        title: 'Shopping List',
        description:
            'A tap-friendly grocery list you can tick off as you shop.',
        category: 'Tasks & Lists',
        details: {
            gameName: 'Grocery Buddy',
            objective:
                'Build a shopping list with quantities and tick items off while shopping.',
            coreMechanics:
                'Add an item with a name and quantity, tap to check off bought items (strikethrough), step quantities up and down, remove items, clear bought.',
            visualStyle:
                'Friendly layout with large tap targets suited to using on a phone.',
            keyElements:
                'Saves the list to localStorage, quantity steppers, checked/strikethrough state, total and remaining counts, clear-bought button.'
        }
    },
    {
        icon: StickyNoteIcon,
        title: 'Sticky Notes',
        description: 'Colorful draggable notes on a board that remembers them.',
        category: 'Tasks & Lists',
        details: {
            gameName: 'Quick Notes',
            objective:
                'Jot down quick colored notes and keep them arranged on a board.',
            coreMechanics:
                'Click to add a note, type directly into it, choose a color, drag to reposition, delete with a corner button.',
            visualStyle:
                'Colorful sticky-note cards on a soft corkboard-style background.',
            keyElements:
                'Persists each note (text, color, position) to localStorage, multiple note colors, inline editing, draggable notes, delete button.'
        }
    },
    {
        icon: FileTextIcon,
        title: 'Markdown Editor',
        description:
            'Write Markdown with a live preview and auto-saved drafts.',
        category: 'Tasks & Lists',
        details: {
            gameName: 'Markdown Studio',
            objective:
                'Write Markdown on the left and see a live HTML preview on the right, with the draft auto-saved.',
            coreMechanics:
                'Type Markdown into a textarea, the preview re-renders live; toolbar buttons insert bold/italic/heading/link; the draft auto-saves as you type.',
            visualStyle:
                'Split-pane editor with a monospace input and a clean rendered preview.',
            keyElements:
                'Auto-saves the draft to localStorage and restores it on reload, supports headings, bold, italic, lists, links and code via a small built-in parser (no external library), live word count, copy-HTML button.'
        }
    },
    {
        icon: TimerIcon,
        title: 'Pomodoro Timer',
        description: 'Focused work sessions with breaks and a session count.',
        category: 'Focus & Learning',
        details: {
            gameName: 'Focus Tomato',
            objective:
                'Work in focused 25-minute sessions with short and long breaks while tracking completed sessions.',
            coreMechanics:
                'Start, pause and reset the timer; it auto-switches between work and break; durations are configurable; an audible chime plays when an interval ends.',
            visualStyle:
                'Large circular countdown with a calm color that shifts between focus and break modes.',
            keyElements:
                'Saves settings and today’s completed-session count to localStorage, work / short-break / long-break cycle, session counter, settings panel, end-of-interval sound.'
        }
    },
    {
        icon: HourglassHalfIcon,
        title: 'Study Timer & Log',
        description: 'Time study sessions by subject and keep a daily log.',
        category: 'Focus & Learning',
        details: {
            gameName: 'Study Log',
            objective:
                'Time study sessions by subject and keep a running log of how long you studied each day.',
            coreMechanics:
                'Pick or type a subject, start and stop a stopwatch; each finished session is saved to a history list with its date and duration.',
            visualStyle:
                'Focused single-column layout with a big timer and a scrollable session history.',
            keyElements:
                'Persists the full session history to localStorage, per-subject and daily totals, delete a logged session, today vs all-time summary.'
        }
    },
    {
        icon: CalendarDaysIcon,
        title: 'Countdown to Event',
        description: 'Live countdowns to exams, birthdays and deadlines.',
        category: 'Focus & Learning',
        details: {
            gameName: 'Countdown',
            objective:
                'Count down to important dates like exams, birthdays, or deadlines.',
            coreMechanics:
                'Add an event with a name and target date/time, see live days/hours/minutes/seconds remaining for each, remove events.',
            visualStyle:
                'Bold countdown cards with large numbers and a colored accent per event.',
            keyElements:
                'Saves all events to localStorage, multiple simultaneous countdowns updating every second, graceful handling of past events, add and delete events.'
        }
    },
    {
        icon: CopyIcon,
        title: 'Flashcards',
        description: 'Build decks of cards and study them by flipping through.',
        category: 'Focus & Learning',
        details: {
            gameName: 'Flip Cards',
            objective:
                'Create decks of question-and-answer cards and study them by flipping through.',
            coreMechanics:
                'Create a deck, add cards with a front and back, enter study mode to flip cards and mark each known or unknown, shuffle the deck.',
            visualStyle:
                'A centered card with a smooth 3D flip animation and simple deck navigation.',
            keyElements:
                'Persists decks and cards to localStorage, multiple decks, flip animation, known/unknown tracking, progress through the deck, shuffle button.'
        }
    },
    {
        icon: CalendarDaysIcon,
        title: 'Daily Planner',
        description: 'Block out your day hour by hour.',
        category: 'Focus & Learning',
        details: {
            gameName: 'Day Blocks',
            objective: 'Plan the day hour by hour with time-blocked entries.',
            coreMechanics:
                'Click an hour slot to add or edit what you’re doing, color-code blocks, clear a slot; the view defaults to today.',
            visualStyle:
                'Vertical hourly timetable from morning to night with colored blocks.',
            keyElements:
                'Saves each day’s plan to localStorage keyed by date, editable hourly slots, color tags, current-hour highlight, clear-day button.'
        }
    },
    {
        icon: CalculatorIcon,
        title: 'Scientific Calculator',
        description: 'Everyday and scientific maths with a saved history.',
        category: 'Calculators & Tools',
        details: {
            gameName: 'Sci Calc',
            objective:
                'Perform everyday and scientific calculations with a history you can scroll back through.',
            coreMechanics:
                'Use buttons or the keyboard for digits and operators including sin/cos/tan, log, square root, powers and parentheses; equals evaluates the expression safely (parse the expression, do not use eval).',
            visualStyle:
                'Dark calculator with a clear display and a grid of well-spaced buttons.',
            keyElements:
                'Saves a calculation history to localStorage, full keyboard support, scientific functions, clear and backspace, click a history entry to reuse it.'
        }
    },
    {
        icon: RadicalIcon,
        title: 'Equation Solver',
        description: 'Solve linear and quadratic equations with steps.',
        category: 'Calculators & Tools',
        details: {
            gameName: 'Solve It',
            objective:
                'Solve linear and quadratic equations and show the working steps.',
            coreMechanics:
                'Enter the coefficients, choose linear or quadratic, and get the solution(s) with a step-by-step explanation.',
            visualStyle:
                'Clean math-focused layout with clearly labeled inputs and a results panel.',
            keyElements:
                'Saves recently solved equations to localStorage, linear (ax+b=0) and quadratic (ax²+bx+c=0) with discriminant handling and real or complex roots, step-by-step working, no external math library.'
        }
    },
    {
        icon: ArrowRightLeftIcon,
        title: 'Unit Converter',
        description: 'Convert length, weight, temperature and volume offline.',
        category: 'Calculators & Tools',
        details: {
            gameName: 'Convert Pro',
            objective:
                'Convert between common units of length, weight, temperature, and volume.',
            coreMechanics:
                'Pick a category, enter a value, choose from and to units; the result updates live; a swap button reverses the units.',
            visualStyle:
                'Simple two-field converter with a category selector and a swap button.',
            keyElements:
                'Remembers the last category and units in localStorage, built-in conversion factors that work offline (no internet), live conversion, swap button, copy-result button.'
        }
    },
    {
        icon: ReceiptIcon,
        title: 'Tip & Bill Splitter',
        description: 'Work out the tip and split a bill between people.',
        category: 'Calculators & Tools',
        details: {
            gameName: 'Split Easy',
            objective:
                'Work out the tip and split a bill evenly between people.',
            coreMechanics:
                'Enter the bill amount, choose a tip percentage (presets or custom), set the number of people; see the tip, total and per-person amounts live.',
            visualStyle:
                'Friendly single-screen calculator with large result figures.',
            keyElements:
                'Remembers the preferred tip percentage in localStorage, preset and custom tip, people stepper, live tip/total/per-person breakdown, round-up option.'
        }
    },
    {
        icon: PiggyBankIcon,
        title: 'Loan Calculator',
        description: 'Estimate repayments with a full amortization table.',
        category: 'Calculators & Tools',
        details: {
            gameName: 'Loan Planner',
            objective:
                'Estimate monthly loan repayments and view an amortization schedule.',
            coreMechanics:
                'Enter the principal, annual interest rate and term in years; calculate the monthly payment and total interest, and generate a payment-by-payment table.',
            visualStyle:
                'Form-and-results layout with a scrollable amortization table.',
            keyElements:
                'Saves the last inputs to localStorage, monthly payment and total-interest summary, full amortization schedule, currency formatting.'
        }
    },
    {
        icon: GraduationCapIcon,
        title: 'GPA Calculator',
        description: 'Compute a GPA from courses, grades and credits.',
        category: 'Calculators & Tools',
        details: {
            gameName: 'GPA Tracker',
            objective:
                'Calculate a grade point average from courses, grades, and credit hours.',
            coreMechanics:
                'Add courses with a grade and credit value; the GPA recalculates live; edit or remove courses.',
            visualStyle:
                'Table-style course list with a prominent GPA readout.',
            keyElements:
                'Saves the course list to localStorage, GPA weighted by credits, configurable grade scale, add/edit/delete courses, total credits.'
        }
    },
    {
        icon: CalendarCheckIcon,
        title: 'Habit Tracker',
        description: 'Check off habits daily and grow your streaks.',
        category: 'Trackers & Journals',
        details: {
            gameName: 'Habit Hero',
            objective:
                'Build habits by checking them off each day and growing a streak.',
            coreMechanics:
                'Add habits, tap today’s box to mark a habit done, view a weekly grid, and watch current and best streaks update.',
            visualStyle:
                'A grid of habits with check marks and a motivating streak badge.',
            keyElements:
                'Persists habits and completion history to localStorage, daily check-ins, current and best streak counters, weekly grid view, add and delete habits.'
        }
    },
    {
        icon: WalletIcon,
        title: 'Expense Tracker',
        description: 'Log spending by category and see where money goes.',
        category: 'Trackers & Journals',
        details: {
            gameName: 'Penny Wise',
            objective:
                'Log expenses by category and see where your money goes.',
            coreMechanics:
                'Add an expense (amount, category, date, note), see a running total and a breakdown by category, delete entries.',
            visualStyle:
                'A list of entries with a summary header and a simple category chart.',
            keyElements:
                'Saves all expenses to localStorage, category breakdown drawn with canvas or CSS bars, monthly total, filter by category, add and delete entries.'
        }
    },
    {
        icon: DropletIcon,
        title: 'Water Intake Tracker',
        description: 'Track daily hydration toward a goal.',
        category: 'Trackers & Journals',
        details: {
            gameName: 'Hydrate',
            objective: 'Track daily water intake toward a personal goal.',
            coreMechanics:
                'Set a daily goal, tap to add a glass or cup, watch a progress ring fill, and start fresh automatically on a new day.',
            visualStyle:
                'Calming blue theme with an animated fill or progress ring.',
            keyElements:
                'Saves the goal and per-day intake to localStorage, animated progress indicator, quick-add buttons, daily history, goal-reached celebration.'
        }
    },
    {
        icon: SmileIcon,
        title: 'Mood Journal',
        description: 'Log a daily mood and note, and look back over time.',
        category: 'Trackers & Journals',
        details: {
            gameName: 'Mood Diary',
            objective:
                'Record a daily mood and a short note, and look back over time.',
            coreMechanics:
                'Pick today’s mood from a set of emoji, add an optional note, and view past entries on a calendar or color heatmap.',
            visualStyle:
                'Soft, warm theme with expressive mood emoji and a month grid.',
            keyElements:
                'Persists entries to localStorage keyed by date, emoji mood picker, optional notes, calendar/heatmap view, ability to edit a past day.'
        }
    }
];

const templatePromptsData: BuilderTemplate[] = [
    ...gameTemplates.map(t => ({...t, kind: 'game' as const})),
    ...appTemplates.map(t => ({...t, kind: 'app' as const}))
];

export default templatePromptsData;
